import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import collections, { findCollection } from "../../../config/collections";
import { resolveImageUrl } from "../../../lib/storage";
import useIsMobile from "../../../hooks/useIsMobile";
import useCollectionImages from "../../../hooks/useCollectionImages";
// ADJUST these three to your folder layout:
import SmartHero from "../smarthero/Smarthero";
import LazyImage from "../work/travelPageTemplate/LazyLoadImages";
import MasonryGrid from "../masonrygrid/MasonryGrid";
import Lightbox from "../lightbox/Lightbox";
import Wordmark from "../wordmark/Wordmark";
import NotFound from "../notfound/notFound";

export default function Collection() {
  const { slug } = useParams();
  const data = findCollection(slug);
  const isMobile = useIsMobile();
  const { images, loading, error } = useCollectionImages(data?.folder);
  const [selected, setSelected] = useState(null);
  const [coverUrl, setCoverUrl] = useState("");

  useEffect(() => {
    let alive = true;
    setCoverUrl("");
    if (data?.cover) {
      resolveImageUrl(data.cover).then((u) => alive && setCoverUrl(u));
    }
    return () => {
      alive = false;
    };
  }, [data]);

  if (!data) return <NotFound />;

  const idx = collections.findIndex((c) => c.slug === data.slug);
  const prev = collections[(idx - 1 + collections.length) % collections.length];
  const next = collections[(idx + 1) % collections.length];
  const paragraphs = data.description.split("\n").filter(Boolean);

  // Essays longer than roughly one viewport of reading flow into two
  // print-style columns on wide screens. Content decides eligibility here;
  // the viewport decides rendering (media query in global.css).
  const TWO_COL_MIN_WORDS = 350;
  const wordCount = data.description.split(/\s+/).filter(Boolean).length;
  const essayClass = `coll-essay${
    wordCount >= TWO_COL_MIN_WORDS ? " coll-essay--columns" : ""
  }`;

  return (
    <div className="page">
      <Helmet>
        <title>{`photoLevs: ${data.title}`}</title>
        <meta name="description" content={data.claim} />
        <meta property="og:title" content={`photoLevs: ${data.title}`} />
        {coverUrl && <meta property="og:image" content={coverUrl} />}
      </Helmet>

      {coverUrl && (
        <div className="coll-hero">
          {/* 200px letterbox; crop position chosen by image content.
              Change the band height here if you ever want to. */}
          <SmartHero src={coverUrl} alt={data.title} height={200} />
        </div>
      )}

      <h1 className="coll-title">{data.title}</h1>
      <p className="coll-year">{data.year}</p>
      <p className="coll-claim">{data.claim}</p>

      <div className={essayClass}>
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {loading && (
        <div className="status">
          <Wordmark text="loading" />
        </div>
      )}

      {error && (
        <div className="status">
          <p className="prose">
            These photographs couldn&rsquo;t load. Refresh the page to try
            again.
          </p>
        </div>
      )}

      {!loading &&
        images.length > 0 &&
        (isMobile ? (
          <div className="coll-stack">
            {images.map((img, i) => (
              <button key={img.name} onClick={() => setSelected(i)}>
                <LazyImage src={img.url} alt={img.caption} />
              </button>
            ))}
          </div>
        ) : (
          <MasonryGrid items={images} onSelect={setSelected} />
        ))}

      <nav className="coll-footer-nav" aria-label="Collections">
        <Link to={`/${prev.slug}`}>&#8592; {prev.title}</Link>
        <Link to={`/${next.slug}`}>{next.title} &#8594;</Link>
      </nav>

      {selected !== null && (
        <Lightbox
          items={images}
          index={selected}
          onClose={() => setSelected(null)}
          onNavigate={setSelected}
        />
      )}
    </div>
  );
}